import { useInfiniteQuery } from '@tanstack/react-query';

import { useAppContext } from '@/context';
import { logout } from '@/utils/auth';
import { getJson } from '@/utils/fetchOptions';

import { useAuthToken } from '..';

type UseContentHubSearchProps = {
  folderId: string | null | undefined;
  fallbackFolderId?: string | null;
  text?: string | null;
  pageSize?: number;
  enabled?: boolean;
  nonce?: number;
};

function getFileExtension(filename: string | undefined) {
  if (!filename) return '';
  const idx = filename.lastIndexOf('.');
  if (idx === -1) return '';
  return filename.slice(idx + 1).toLowerCase();
}

export function isVideoFilename(filename: string | undefined) {
  const ext = getFileExtension(filename);
  return ['mp4', 'mov', 'webm', 'ogg', 'avi', 'm4v'].includes(ext);
}

export function useContentHubSearch({
  folderId,
  fallbackFolderId = null,
  text,
  pageSize = 24,
  enabled = true,
  nonce
}: UseContentHubSearchProps) {
  const { setAlert } = useAppContext();
  const token = useAuthToken();

  const query = useInfiniteQuery({
    queryKey: [
      'client-contenthub-search',
      folderId,
      fallbackFolderId ?? '',
      text ?? '',
      pageSize,
      nonce
    ],
    enabled: Boolean(enabled && token && folderId),
    queryFn: async ({ pageParam }) => {
      const pageNumber =
        typeof pageParam === 'number' && Number.isFinite(pageParam) ? pageParam : 0;

      const buildUrl = (id: string, textOverride?: string | null) => {
        const params = new URLSearchParams();
        params.set('folder_id', id);
        params.set('page_number', String(pageNumber));
        params.set('page_size', String(pageSize));
        const effectiveText = textOverride ?? text;
        if (effectiveText) params.set('text', effectiveText);
        return `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/contenthub/search?${params.toString()}`;
      };

      const fetchPage = async (id: string, textOverride?: string | null) => {
        const url = buildUrl(id, textOverride);
        return getJson({ url, token }) as Promise<
          Api.Client.ContentHubMediaPage & ResponseObject & ResponseExpiredToken
        >;
      };

      const tryCaseVariantsIfEmpty = async (
        id: string,
        response: Api.Client.ContentHubMediaPage & ResponseObject & ResponseExpiredToken
      ) => {
        if (!text) return response;
        if (!Array.isArray((response as TypeAny)?.medias)) return response;
        if ((response as TypeAny).medias.length > 0) return response;

        const upper = text.toUpperCase();
        if (upper !== text) {
          const upperResp = await fetchPage(id, upper);
          if (
            Array.isArray((upperResp as TypeAny)?.medias) &&
            (upperResp as TypeAny).medias.length > 0
          ) {
            return upperResp;
          }
        }

        const lower = text.toLowerCase();
        if (lower !== text) {
          const lowerResp = await fetchPage(id, lower);
          if (
            Array.isArray((lowerResp as TypeAny)?.medias) &&
            (lowerResp as TypeAny).medias.length > 0
          ) {
            return lowerResp;
          }
        }

        return response;
      };

      let response = await fetchPage(String(folderId));

      if ((response as ResponseExpiredToken).isExpired) {
        logout();
      }

      if ('code' in response) {
        setAlert(response.code);
      }

      const hasMediasArray = Array.isArray((response as TypeAny)?.medias);
      if (!hasMediasArray && fallbackFolderId && fallbackFolderId !== folderId) {
        let fallbackResponse = await fetchPage(String(fallbackFolderId));

        if ((fallbackResponse as ResponseExpiredToken).isExpired) {
          logout();
        }

        if ('code' in fallbackResponse) {
          setAlert(fallbackResponse.code);
        }

        fallbackResponse = await tryCaseVariantsIfEmpty(String(fallbackFolderId), fallbackResponse);
        return {
          ...(fallbackResponse as TypeAny),
          contenthubUsedFallback: true,
          contenthubResolvedFolderId: String(fallbackFolderId)
        } as TypeAny;
      }

      response = await tryCaseVariantsIfEmpty(String(folderId), response);
      return {
        ...(response as TypeAny),
        contenthubUsedFallback: false,
        contenthubResolvedFolderId: String(folderId)
      } as TypeAny;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.has_more) return undefined;
      const current = typeof lastPage.page_number === 'number' ? lastPage.page_number : 0;
      return current + 1;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: 0,
    retry: 1
  });

  const medias = (query.data?.pages ?? []).flatMap((p) => p.medias ?? []);
  const hasMore = Boolean(query.hasNextPage);
  const usedFallback = Boolean(
    (query.data?.pages ?? []).some((p: TypeAny) => Boolean(p?.contenthubUsedFallback))
  );
  const resolvedFolderId = (query.data?.pages?.[0] as TypeAny)?.contenthubResolvedFolderId ?? null;

  return {
    ...query,
    medias,
    hasMore,
    isFetchingNextPage: query.isFetchingNextPage,
    usedFallback,
    resolvedFolderId
  };
}
